import { type ZodRawShape, ZodType, z } from 'zod';

import { type AnyZodSchema } from '@Common/types/zod/utils';
import { createZodStringParser, isZodStringParser } from '@Common/types/zod/zod-parse-string';

export const ZOD_PARAMS_BRAND = 'internal/params';
export type ZodParamsBrand = typeof ZOD_PARAMS_BRAND;
export const ZOD_HASHED_ID_PARAMS_BRAND = 'internal/hashed_id_params';
export type ZodHashedIdParamsBrand = typeof ZOD_HASHED_ID_PARAMS_BRAND;

export type BrandedZodParams<T extends AnyZodSchema> = T & z.$brand<ZodParamsBrand>;

export type BrandedZodHashedIdParams<T extends AnyZodSchema> = T & z.$brand<ZodParamsBrand>;

const emptyObjectSchema = z.object({});

export type BrandedRouteNotSpecifiedParams =
  | BrandedZodParams<typeof emptyObjectSchema>
  | BrandedZodHashedIdParams<typeof emptyObjectSchema>
  | undefined;

export type BrandedParamsSchema = ReturnType<
  typeof createZodParamsValidator | typeof createZodParamsValidatorWithEffects
>;
export type BrandedHashedIdParamsSchema = ReturnType<typeof createZodHashIdParamsValidator>;

// eslint-disable-next-line complexity, max-statements
function getFromStringValidator(validator: ZodType): ZodType {
  if (isZodStringParser(validator)) {
    return validator;
  }

  if (validator instanceof z.ZodArray) {
    const elementSchema = validator.element as ZodType;
    const processedElementSchema = getFromStringValidator(elementSchema);

    const arraySchema = z.array(processedElementSchema);

    const preprocessSchema = z.preprocess((input: unknown): unknown[] => {
      if (typeof input === 'string' && input.startsWith('[') && input.endsWith(']')) {
        try {
          const parsed: unknown = JSON.parse(input.replace(/'/g, '"'));

          return Array.isArray(parsed) ? parsed : [parsed];
        } catch {
          // Ignore parse errors, fallback to array handling
        }
      }
      return Array.isArray(input) ? input : [input];
    }, arraySchema);

    return preprocessSchema;
  }

  if (validator instanceof z.ZodString || validator instanceof z.ZodEnum) {
    return validator;
  }

  if (validator instanceof z.ZodLiteral) {
    return createZodStringParser(
      validator,
      z
        .string()
        .transform((value) => {
          if (value === 'true' || value === 'false') {
            return value === 'true';
          }
          if (!isNaN(Number(value)) && value.trim() !== '') {
            return Number(value);
          }
          return value;
        })
        .meta({ override: { type: 'string' } }),
    );
  }

  if (validator instanceof z.ZodNumber) {
    return createZodStringParser(
      validator,
      z
        .string()
        .transform((value) => (value.trim() !== '' ? Number(value) : NaN))
        .meta({ override: { type: 'number' } }),
    );
  }

  if (validator instanceof z.ZodBoolean) {
    return createZodStringParser(
      validator,
      z
        .enum(['true', 'false'])
        .transform((value) => value === 'true')
        .meta({ override: { type: 'boolean' } }),
    );
  }

  if (validator instanceof z.ZodOptional) {
    return getFromStringValidator(validator.unwrap() as ZodType).optional();
  }

  if (validator instanceof z.ZodUnion) {
    const newOptions = validator.options.map((option) => getFromStringValidator(option as ZodType));
    return z.union(newOptions as [ZodType, ...ZodType[]]);
  }

  if (validator instanceof z.ZodDefault) {
    const { def } = validator;
    return getFromStringValidator(def.innerType as ZodType).default(def.defaultValue);
  }

  if (validator instanceof z.ZodObject) {
    return validator;
  }

  return validator;
}
export const brandParams = <S extends AnyZodSchema>(schema: S) => {
  return schema as S & z.$brand<ZodParamsBrand>;
};

export function createZodParamsValidator<
  T extends {
    [key: string]: ZodType<unknown>;
  },
>(validator: z.ZodObject<T>) {
  const newShape = enrichZodObjectParams(validator.shape);

  const obj = z.object(newShape);

  return brandParams(obj as unknown as typeof validator);
}

export function createZodParamsValidatorWithEffects<
  T extends {
    [key: string]: ZodType<unknown>;
  },
>(validator: z.ZodObject<T>) {
  const newShape = enrichZodObjectParams(validator.shape);
  const enrichedSchema = z.object(newShape);

  const finalSchema = enrichedSchema.pipe(validator);

  return brandParams(finalSchema as unknown as typeof validator);
}

export const brandHashIdParam = <S extends AnyZodSchema>(schema: S) => {
  return schema as S & z.$brand<ZodHashedIdParamsBrand>;
};

export function createZodHashIdParamsValidator<
  T extends {
    [key: string]: ZodType<unknown>;
  },
>(validator: z.ZodObject<T>) {
  return brandHashIdParam(validator);
}

function handleObjectZodParamsValidator<T extends ZodRawShape>(objectSchema: z.ZodObject<T>) {
  return z.object(enrichZodObjectParams(objectSchema.shape)).loose();
}

export function createZodParamsValidatorZodIntersection<
  T extends AnyZodSchema,
  U extends AnyZodSchema,
>(validator: z.ZodIntersection<T, U>): BrandedZodParams<z.ZodIntersection<T, U>> {
  const { left, right } = validator.def;

  if (left instanceof z.ZodObject && right instanceof z.ZodUnion) {
    const newZodObject = handleObjectZodParamsValidator(left);

    const newUnionOptions = right.options.map((unionOption) => {
      if (unionOption instanceof z.ZodObject) {
        const newUnionObject = z.object(enrichZodObjectParams(unionOption.shape));

        return z.object({
          ...newZodObject.shape,
          ...newUnionObject.shape,
        });
      }
      return unionOption;
    });

    const finalSchema = z.union(newUnionOptions as [AnyZodSchema, ...AnyZodSchema[]]);

    return brandParams(finalSchema) as unknown as BrandedZodParams<z.ZodIntersection<T, U>>;
  }

  throw new Error('Unexpected intersection types. Supported: ZodObject.and(ZodUnion)');
}

export function createZodParamsValidatorZodNestedIntersection<
  T extends AnyZodSchema,
  U extends AnyZodSchema,
>(validator: z.ZodIntersection<T, U>): BrandedZodParams<z.ZodIntersection<T, U>> {
  let baseObject!: z.ZodObject<ZodRawShape>;
  let innerObject!: z.ZodObject<ZodRawShape>;
  let unionSchema!: z.ZodUnion;

  const { left, right } = validator.def;

  // Obj.and(Obj.and(Union))
  if (
    left instanceof z.ZodObject &&
    right instanceof z.ZodIntersection &&
    right.def.left instanceof z.ZodObject &&
    right.def.right instanceof z.ZodUnion
  ) {
    baseObject = left;
    innerObject = right.def.left;

    unionSchema = right.def.right;
  }
  // (Obj.and(Obj)).and(Union)
  else if (
    left instanceof z.ZodIntersection &&
    left.def.left instanceof z.ZodObject &&
    left.def.right instanceof z.ZodObject &&
    right instanceof z.ZodUnion
  ) {
    baseObject = left.def.left;
    innerObject = left.def.right;

    unionSchema = right;
  } else {
    throw new Error('Unexpected nested intersection structure.');
  }

  const processedBaseObject = handleObjectZodParamsValidator(baseObject);
  const processedInnerObject = handleObjectZodParamsValidator(innerObject);

  const combinedBase = z.object({
    ...processedBaseObject.shape,
    ...processedInnerObject.shape,
  });

  const newUnionOptions = unionSchema.options.map((option) => {
    if (option instanceof z.ZodObject) {
      const processedOption = handleObjectZodParamsValidator(option as z.ZodObject<ZodRawShape>);

      return z.object({
        ...combinedBase.shape,
        ...processedOption.shape,
      });
    }
    return option;
  }) as [AnyZodSchema, ...AnyZodSchema[]];

  const finalSchema = z.union(newUnionOptions);

  return brandParams(finalSchema) as unknown as BrandedZodParams<z.ZodIntersection<T, U>>;
}

function enrichZodObjectParams(object: ZodRawShape) {
  const entries = Object.entries(object) as [string, ZodType][];

  return Object.fromEntries(
    entries.map(([key, propertyValidator]) => [key, getFromStringValidator(propertyValidator)]),
  );
}
